/**
 * ErrorLog
 *
 * Error log viewer for debugging connection issues.
 * Fetches error events from Supabase analytics_events table.
 * Includes time and error type filters, grouping by session, and clearing.
 *
 * Access at: /admin/errors
 */

import { useState, useEffect, useMemo } from 'react';
import { ArrowLeft } from '@phosphor-icons/react/dist/ssr/ArrowLeft';
import { ArrowClockwise } from '@phosphor-icons/react/dist/ssr/ArrowClockwise';
import { Trash } from '@phosphor-icons/react/dist/ssr/Trash';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { CaretRight } from '@phosphor-icons/react/dist/ssr/CaretRight';
import { COLORS, SPACING, TYPOGRAPHY, FONT_FAMILY, SHADOWS } from '../../styles/UISystem';
import { supabase } from '../../services/supabase';
import { mapAnalyticsEvent } from '../../services/analytics/eventLogger';

// Time filter options
const TIME_FILTERS = {
  today: { label: 'Today', days: 0 },
  '7days': { label: 'Last 7 days', days: 7 },
  '30days': { label: 'Last 30 days', days: 30 },
  all: { label: 'All time', days: null },
};

// Human-readable error descriptions and severity — source of truth for error triage.
// Keys match DB event_type values (logConnectionError prepends 'error_' to caller's string).
const ERROR_CATALOG = {
  error_user_redirect: {
    label: 'QR Scan Failed',
    description:
      "Anon scanned the QR but couldn't find an active session — they're stuck on the error screen.",
    severity: 'critical',
  },
  error_session_activation: {
    label: 'Session Setup Failed',
    description:
      "Profile's session couldn't activate in the database. QR shows but scans will fail.",
    severity: 'critical',
  },
  error_websocket_profile: {
    label: 'Profile Lost Connection',
    description:
      "Profile's WebSocket failed to connect. QR works but Profile never receives messages.",
    severity: 'critical',
  },
  error_websocket_anon: {
    label: 'Anon Lost Connection',
    description:
      "Anon's WebSocket failed after scanning. Usually a network issue on Anon's device.",
    severity: 'critical',
  },
  error_connection_not_confirmed: {
    label: 'No Response From Profile',
    description:
      'Anon wrote to DB and joined WebSocket, but Profile never sent confirmation or timed out.',
    severity: 'high',
  },
  error_shared_fields_fallback: {
    label: 'Missing Contact Fields',
    description:
      'Profile data arrived without sharedFields array. Contact types may be incomplete.',
    severity: 'medium',
  },
  error_message_send: {
    label: 'Message Not Delivered',
    description:
      "Chat message sent on a channel that wasn't properly joined. Appears sent but never arrives.",
    severity: 'medium',
  },
  error_database: {
    label: 'Database Error',
    description:
      'A Supabase operation failed (session tracking, connection writes, etc). Check error details.',
    severity: 'medium',
  },
  error_chat_persist: {
    label: 'Chat Not Saved',
    description:
      "Message sent successfully but failed to persist to the database. Won't appear on return visits.",
    severity: 'low',
  },
  error_chat_load: {
    label: 'Chat History Missing',
    description: 'Failed to load previous messages from database. Current session works fine.',
    severity: 'low',
  },
  // Written by the sms-approve function (Fraud Guard feedback to Twilio).
  error_sms_approve: {
    label: 'Login Not Reported to Twilio',
    description:
      "A completed login couldn't be marked approved in Twilio Verify. Logins still work; only Twilio's Fraud Guard misses the feedback. metadata.message has Twilio's reason (numbers masked).",
    severity: 'medium',
  },
  error_sms_gate: {
    label: 'Login Code Not Sent',
    description:
      'The SMS gate (Send SMS hook) failed — nobody gets a login code while it repeats. metadata.stage: check (rule query), send (Twilio Verify) or payload. Turn the hook off to restore sending.',
    severity: 'critical',
  },
  error_location_share_failed: {
    label: 'Live Location Failed',
    description:
      'Tried to start live-location sharing but it failed — denied permission, GPS timeout, or no neighborhood resolved. Check metadata.reason + userAgent (the device detail Emily lacked).',
    severity: 'medium',
  },
  // Written by the server (042 stamp trigger), one per event it kept with the
  // profile blanked — a rising count is a client gap worth chasing.
  error_analytics_profile_unproven: {
    label: 'Event Sent Before Profile Saved',
    description:
      "An event named a profile this phone couldn't prove was its own (saved before the server had it, or sign-in lost). The event was kept without the name.",
    severity: 'medium',
  },
};

const SEVERITY_COLORS = {
  critical: { bg: '#fee2e2', text: '#dc2626' },
  high: { bg: '#fff7ed', text: '#ea580c' },
  medium: { bg: '#fefce8', text: '#ca8a04' },
  low: { bg: '#f3f4f6', text: '#6b7280' },
};

const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

const getCatalogEntry = (eventType) => ERROR_CATALOG[eventType] || null;

const getSeverity = (eventType) => getCatalogEntry(eventType)?.severity || null;

// Error type options (populated from data)
const ERROR_TYPE_ALL = 'all';
const SEVERITY_ALL = 'all';

const ErrorLog = () => {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [timeFilter, setTimeFilter] = useState('7days');
  const [typeFilter, setTypeFilter] = useState(ERROR_TYPE_ALL);
  const [severityFilter, setSeverityFilter] = useState(SEVERITY_ALL);
  const [groupBySession, setGroupBySession] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Get unique error types from fetched data
  const errorTypes = useMemo(() => {
    const types = new Set(errors.map((e) => e.event_type));
    return Array.from(types).sort();
  }, [errors]);

  // Filter errors based on selected filters
  const filteredErrors = useMemo(() => {
    let filtered = errors;

    // Apply time filter
    if (TIME_FILTERS[timeFilter]?.days !== null) {
      const days = TIME_FILTERS[timeFilter].days;
      const cutoff = new Date();
      if (days === 0) {
        // Today - start of day
        cutoff.setHours(0, 0, 0, 0);
      } else {
        cutoff.setDate(cutoff.getDate() - days);
      }
      filtered = filtered.filter((e) => new Date(e.created_at) >= cutoff);
    }

    // Apply type filter
    if (typeFilter !== ERROR_TYPE_ALL) {
      filtered = filtered.filter((e) => e.event_type === typeFilter);
    }

    // Apply severity filter
    if (severityFilter !== SEVERITY_ALL) {
      filtered = filtered.filter((e) => getSeverity(e.event_type) === severityFilter);
    }

    return filtered;
  }, [errors, timeFilter, typeFilter, severityFilter]);

  // Group errors by chatRoomId
  const groupedErrors = useMemo(() => {
    if (!groupBySession) return null;

    const groups = {};
    filteredErrors.forEach((error) => {
      const chatRoomId = error.event_data?.chatRoomId || 'unknown';
      if (!groups[chatRoomId]) {
        groups[chatRoomId] = {
          chatRoomId,
          errors: [],
          firstError: error,
          errorTypes: new Set(),
          profileUsername: error.event_data?.profileUsername || null,
        };
      }
      groups[chatRoomId].errors.push(error);
      groups[chatRoomId].errorTypes.add(error.event_type);
    });

    // Convert to array and sort by first error timestamp (newest first)
    return Object.values(groups).sort(
      (a, b) => new Date(b.firstError.created_at) - new Date(a.firstError.created_at)
    );
  }, [filteredErrors, groupBySession]);

  // Toggle group expansion
  const toggleGroup = (chatRoomId) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(chatRoomId)) {
        next.delete(chatRoomId);
      } else {
        next.add(chatRoomId);
      }
      return next;
    });
  };

  // Clear a single error
  const clearError = async (errorId) => {
    try {
      await supabase.from('analytics_events').delete().eq('id', errorId);
      fetchErrors();
    } catch (err) {
      console.error('Failed to clear error:', err);
    }
  };

  // Clear all errors for a session (chatRoomId)
  const clearSession = async (chatRoomId) => {
    try {
      // Get all error IDs for this chatRoomId
      const errorsToDelete = filteredErrors.filter(
        (e) => (e.event_data?.chatRoomId || 'unknown') === chatRoomId
      );
      const ids = errorsToDelete.map((e) => e.id);

      if (ids.length > 0) {
        await supabase.from('analytics_events').delete().in('id', ids);
        fetchErrors();
      }
    } catch (err) {
      console.error('Failed to clear session errors:', err);
    }
  };

  // Clear all errors
  const clearAll = async () => {
    if (!window.confirm('Clear all errors? This cannot be undone.')) return;
    try {
      const ids = filteredErrors.map((e) => e.id);
      if (ids.length > 0) {
        await supabase.from('analytics_events').delete().in('id', ids);
        fetchErrors();
      }
    } catch (err) {
      console.error('Failed to clear all errors:', err);
    }
  };

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('analytics_events')
        .select('*')
        .eq('event_type', 'error')
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) {
        console.error('Failed to fetch errors:', error);
        return;
      }

      setErrors((data || []).map(mapAnalyticsEvent));
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching errors:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchErrors();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getErrorTypeLabel = (eventType) => {
    const entry = getCatalogEntry(eventType);
    if (entry) return entry.label;
    return eventType.replace('error_', '').replace(/_/g, ' ');
  };

  const SeverityBadge = ({ eventType }) => {
    const severity = getSeverity(eventType);
    if (!severity) return null;
    const colors = SEVERITY_COLORS[severity];
    return (
      <span
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          padding: '1px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {severity}
      </span>
    );
  };

  const ErrorDescription = ({ eventType }) => {
    const entry = getCatalogEntry(eventType);
    if (!entry) return null;
    return (
      <div style={{ fontSize: '12px', color: COLORS.DARK_GRAY, marginTop: '2px' }}>
        {entry.description}
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        fontFamily: FONT_FAMILY.PRIMARY,
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: COLORS.WHITE,
          padding: SPACING.MD,
          borderBottom: '1px solid #eee',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.SM }}>
          <button
            onClick={() => (window.location.href = '/admin/dashboard')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: SPACING.XS,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ArrowLeft size={24} color={COLORS.TEXT} />
          </button>
          <h1 style={{ ...TYPOGRAPHY.HEADER_LARGE, margin: 0 }}>Error Log</h1>
          {filteredErrors.length > 0 && (
            <span
              style={{
                backgroundColor: '#dc2626',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 600,
                marginLeft: SPACING.XS,
              }}
            >
              {filteredErrors.length}
            </span>
          )}
        </div>
        <button
          onClick={fetchErrors}
          disabled={loading}
          style={{
            background: 'none',
            border: 'none',
            cursor: loading ? 'wait' : 'pointer',
            padding: SPACING.XS,
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.XS,
            color: COLORS.DARK_GRAY,
          }}
        >
          <ArrowClockwise size={20} className={loading ? 'spin' : ''} />
          <span style={{ fontSize: '14px' }}>Refresh</span>
        </button>
      </div>

      {/* Filters bar */}
      <div
        style={{
          backgroundColor: COLORS.WHITE,
          padding: `${SPACING.SM} ${SPACING.MD}`,
          borderBottom: '1px solid #eee',
          display: 'flex',
          gap: SPACING.MD,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {/* Time filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.XS }}>
          <label style={{ fontSize: '12px', color: COLORS.DARK_GRAY }}>Time:</label>
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: `1px solid ${COLORS.LIGHT_GRAY}`,
              fontSize: '13px',
              backgroundColor: COLORS.WHITE,
            }}
          >
            {Object.entries(TIME_FILTERS).map(([key, { label }]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Error type filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.XS }}>
          <label style={{ fontSize: '12px', color: COLORS.DARK_GRAY }}>Type:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: `1px solid ${COLORS.LIGHT_GRAY}`,
              fontSize: '13px',
              backgroundColor: COLORS.WHITE,
            }}
          >
            <option value={ERROR_TYPE_ALL}>All types</option>
            {errorTypes.map((type) => (
              <option key={type} value={type}>
                {getErrorTypeLabel(type)}
              </option>
            ))}
          </select>
        </div>

        {/* Severity filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.XS }}>
          <label style={{ fontSize: '12px', color: COLORS.DARK_GRAY }}>Severity:</label>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: `1px solid ${COLORS.LIGHT_GRAY}`,
              fontSize: '13px',
              backgroundColor: COLORS.WHITE,
            }}
          >
            <option value={SEVERITY_ALL}>All</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        {/* Group by session toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.XS }}>
          <label
            style={{
              fontSize: '12px',
              color: COLORS.DARK_GRAY,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={groupBySession}
              onChange={(e) => setGroupBySession(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Group by session
          </label>
        </div>

        {/* Stats and Clear All */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: SPACING.MD, alignItems: 'center' }}>
          <span style={{ color: COLORS.DARK_GRAY, fontSize: '13px' }}>
            {filteredErrors.length} of {errors.length} error{errors.length !== 1 ? 's' : ''}
            {groupBySession && groupedErrors && ` (${groupedErrors.length} sessions)`}
          </span>
          {filteredErrors.length > 0 && (
            <button
              onClick={clearAll}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#dc2626',
                fontSize: '12px',
              }}
            >
              <Trash size={14} />
              Clear All
            </button>
          )}
          {lastRefresh && (
            <span style={{ color: COLORS.DARK_GRAY, fontSize: '12px' }}>
              Updated: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Error list */}
      <div style={{ padding: SPACING.MD }}>
        {loading && errors.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: SPACING.XL,
              color: COLORS.DARK_GRAY,
            }}
          >
            Loading errors...
          </div>
        ) : filteredErrors.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: SPACING.XL,
              color: COLORS.DARK_GRAY,
            }}
          >
            {errors.length === 0 ? 'No errors logged yet' : 'No errors match filters'}
          </div>
        ) : groupBySession && groupedErrors ? (
          /* Grouped view */
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.SM }}>
            {groupedErrors.map((group) => {
              const isExpanded = expandedGroups.has(group.chatRoomId);
              return (
                <div
                  key={group.chatRoomId}
                  style={{
                    backgroundColor: COLORS.WHITE,
                    borderRadius: '8px',
                    boxShadow: SHADOWS.TIER1,
                    overflow: 'hidden',
                  }}
                >
                  {/* Group header */}
                  <div
                    onClick={() => toggleGroup(group.chatRoomId)}
                    style={{
                      padding: SPACING.MD,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING.SM,
                      borderBottom: isExpanded ? '1px solid #f3f4f6' : 'none',
                    }}
                  >
                    {isExpanded ? (
                      <CaretDown size={16} color={COLORS.DARK_GRAY} />
                    ) : (
                      <CaretRight size={16} color={COLORS.DARK_GRAY} />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.SM }}>
                        <code
                          style={{
                            backgroundColor: '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '13px',
                          }}
                        >
                          ...{group.chatRoomId.slice(-8)}
                        </code>
                        {group.profileUsername && (
                          <span style={{ fontSize: '13px', color: COLORS.TEXT }}>
                            {group.profileUsername}
                          </span>
                        )}
                        <span
                          style={{
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                          }}
                        >
                          {group.errors.length}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: COLORS.DARK_GRAY,
                          marginTop: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: SPACING.XS,
                          flexWrap: 'wrap',
                        }}
                      >
                        {(() => {
                          const severities = Array.from(group.errorTypes)
                            .map(getSeverity)
                            .filter(Boolean);
                          const highest = severities.sort(
                            (a, b) => SEVERITY_ORDER[a] - SEVERITY_ORDER[b]
                          )[0];
                          if (!highest) return null;
                          const colors = SEVERITY_COLORS[highest];
                          return (
                            <span
                              style={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                padding: '1px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                              }}
                            >
                              {highest}
                            </span>
                          );
                        })()}
                        <span>
                          {Array.from(group.errorTypes).map(getErrorTypeLabel).join(', ')}
                        </span>
                      </div>
                    </div>
                    <span style={{ color: COLORS.DARK_GRAY, fontSize: '12px' }}>
                      {formatDate(group.firstError.created_at)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSession(group.chatRoomId);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        color: '#dc2626',
                      }}
                      title="Clear session errors"
                    >
                      <Trash size={16} />
                    </button>
                  </div>

                  {/* Expanded errors */}
                  {isExpanded && (
                    <div style={{ padding: SPACING.SM }}>
                      {group.errors.map((error) => (
                        <div
                          key={error.id}
                          style={{
                            padding: SPACING.SM,
                            borderBottom: '1px solid #f9fafb',
                            position: 'relative',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: SPACING.XS,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: SPACING.XS,
                              }}
                            >
                              <SeverityBadge eventType={error.event_type} />
                              <span
                                style={{
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: COLORS.TEXT,
                                }}
                              >
                                {getErrorTypeLabel(error.event_type)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.XS }}>
                              <span style={{ color: COLORS.DARK_GRAY, fontSize: '11px' }}>
                                {formatDate(error.created_at)}
                              </span>
                              <button
                                onClick={() => clearError(error.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '2px',
                                  color: COLORS.DARK_GRAY,
                                }}
                                title="Clear this error"
                              >
                                <Trash size={12} />
                              </button>
                            </div>
                          </div>
                          <ErrorDescription eventType={error.event_type} />
                          {error.event_data?.message && (
                            <div
                              style={{
                                fontSize: '12px',
                                color: COLORS.TEXT,
                                marginTop: '4px',
                                fontFamily: 'monospace',
                                backgroundColor: '#f9fafb',
                                padding: '4px 6px',
                                borderRadius: '4px',
                              }}
                            >
                              {error.event_data.message}
                            </div>
                          )}
                          {error.event_data?.pageLoadId && (
                            <div
                              style={{
                                fontSize: '11px',
                                color: COLORS.DARK_GRAY,
                                marginTop: '2px',
                              }}
                            >
                              pageLoad: {error.event_data.pageLoadId}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Ungrouped view (original) */
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.SM }}>
            {filteredErrors.map((error) => (
              <div
                key={error.id}
                style={{
                  backgroundColor: COLORS.WHITE,
                  borderRadius: '8px',
                  padding: SPACING.MD,
                  boxShadow: SHADOWS.TIER1,
                  position: 'relative',
                }}
              >
                {/* Clear button */}
                <button
                  onClick={() => clearError(error.id)}
                  style={{
                    position: 'absolute',
                    top: SPACING.SM,
                    right: SPACING.SM,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: COLORS.DARK_GRAY,
                  }}
                  title="Clear this error"
                >
                  <Trash size={16} />
                </button>

                {/* Error header */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: SPACING.XS,
                    paddingRight: '30px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.XS }}>
                    <SeverityBadge eventType={error.event_type} />
                    <span style={{ fontSize: '14px', fontWeight: 600, color: COLORS.TEXT }}>
                      {getErrorTypeLabel(error.event_type)}
                    </span>
                  </div>
                  <span style={{ color: COLORS.DARK_GRAY, fontSize: '12px' }}>
                    {formatDate(error.created_at)}
                  </span>
                </div>

                <ErrorDescription eventType={error.event_type} />

                {/* Error details */}
                <div style={{ fontSize: '14px', color: COLORS.TEXT, marginTop: SPACING.SM }}>
                  {error.event_data?.profileUsername && (
                    <div style={{ marginBottom: SPACING.XS }}>
                      <strong>Profile:</strong> {error.event_data.profileUsername}
                    </div>
                  )}
                  {error.event_data?.message && (
                    <div style={{ marginBottom: SPACING.XS }}>
                      <strong>Message:</strong> {error.event_data.message}
                    </div>
                  )}
                  {error.event_data?.code && (
                    <div style={{ marginBottom: SPACING.XS }}>
                      <strong>Code:</strong>{' '}
                      <code
                        style={{
                          backgroundColor: '#f3f4f6',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '13px',
                        }}
                      >
                        {error.event_data.code}
                      </code>
                    </div>
                  )}
                  {error.event_data?.chatRoomId && (
                    <div style={{ marginBottom: SPACING.XS }}>
                      <strong>Chat Room:</strong>{' '}
                      <code
                        style={{
                          backgroundColor: '#f3f4f6',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '13px',
                        }}
                      >
                        {error.event_data.chatRoomId.slice(-8)}
                      </code>
                    </div>
                  )}
                  {error.event_data?.profileUserId && (
                    <div style={{ marginBottom: SPACING.XS }}>
                      <strong>Profile ID:</strong>{' '}
                      <code
                        style={{
                          backgroundColor: '#f3f4f6',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '13px',
                        }}
                      >
                        {error.event_data.profileUserId}
                      </code>
                    </div>
                  )}
                  {error.event_data?.operation && (
                    <div style={{ marginBottom: SPACING.XS }}>
                      <strong>Operation:</strong> {error.event_data.operation}
                    </div>
                  )}
                  {error.event_data?.attempts && (
                    <div style={{ marginBottom: SPACING.XS }}>
                      <strong>Attempts:</strong> {error.event_data.attempts}
                    </div>
                  )}
                </div>

                {/* User agent (collapsed by default) */}
                {error.event_data?.userAgent && (
                  <details style={{ marginTop: SPACING.SM }}>
                    <summary
                      style={{
                        cursor: 'pointer',
                        color: COLORS.DARK_GRAY,
                        fontSize: '12px',
                      }}
                    >
                      Device info
                    </summary>
                    <div
                      style={{
                        marginTop: SPACING.XS,
                        padding: SPACING.SM,
                        backgroundColor: '#f9fafb',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        color: COLORS.DARK_GRAY,
                      }}
                    >
                      {error.event_data.userAgent}
                    </div>
                  </details>
                )}

                {/* Branch */}
                <div
                  style={{
                    marginTop: SPACING.SM,
                    paddingTop: SPACING.SM,
                    borderTop: '1px solid #f3f4f6',
                    display: 'flex',
                    gap: SPACING.MD,
                    fontSize: '12px',
                    color: COLORS.DARK_GRAY,
                  }}
                >
                  <span>{error.event_type}</span>
                  <span>Branch: {error.branch || 'unknown'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Spin animation for refresh button */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default ErrorLog;
